/**
 * The status a comment can be in.
 *
 * One ladder plus one exit. Every extra state is a state that goes stale —
 * someone has to remember to move things into it, and a status nobody maintains
 * is worse than no status, because it reads as current when it is not. The test
 * each of these passes: something specific clears it, and someone specific is
 * responsible for doing so.
 *
 * `wont_fix` is the honest end for rejected feedback. Without it, a comment we
 * disagree with either sits open forever or gets marked resolved, and "resolved"
 * then stops meaning anything.
 *
 * `assigned` and `in_review` exist because work is now handed to someone else —
 * a person or an agent — and handing off has two edges.
 *
 * `assigned` is the near edge: triaged, understood, queued for whoever is named
 * in `assignee`. It is not the same as having an assignee, which is why it is a
 * status and not inferred from one — you routinely put a name on something while
 * still deciding what to do about it, and an agent must not pick that up. An
 * agent clears it by starting work.
 *
 * `in_review` is the far edge, and the one that protects the meaning of
 * `resolved`. Without it, a fix and a *verified* fix are the same state, so
 * `resolved` decays from "checked" to "somebody said so" — the exact rot
 * `wont_fix` was introduced to prevent. Whoever raised the comment clears it.
 */
export const STATUSES = [
  'open',
  'acknowledged',
  'assigned',
  'in_progress',
  'in_review',
  'resolved',
  'wont_fix',
] as const;

export type Status = (typeof STATUSES)[number];

export function isStatus(v: unknown): v is Status {
  return typeof v === 'string' && (STATUSES as readonly string[]).includes(v);
}

/** Position on the ladder. `wont_fix` deliberately shares the end with `resolved`
 *  — it is an exit, not a further step, and ordering it beyond `resolved` would
 *  imply a progression that does not exist. */
const ORDER: Record<Status, number> = {
  open: 0,
  acknowledged: 1,
  assigned: 2,
  in_progress: 3,
  in_review: 4,
  resolved: 5,
  wont_fix: 5,
};

export function ordinal(status: Status): number {
  return ORDER[status];
}

/** Done with, either way. These are hidden by default in the widget and filtered
 *  out of the "needs attention" counts. */
export function isClosed(status: Status): boolean {
  return status === 'resolved' || status === 'wont_fix';
}

const LABELS: Record<Status, string> = {
  open: 'Open',
  acknowledged: 'Acknowledged',
  assigned: 'Assigned',
  in_progress: 'In progress',
  in_review: 'Needs review',
  resolved: 'Resolved',
  wont_fix: "Won't fix",
};

export function formatStatus(status: Status): string {
  return LABELS[status];
}

/** Past-tense phrasing for the timeline entry a status change leaves behind. */
export function statusSentence(status: Status, actor: string): string {
  switch (status) {
    case 'open':
      return `${actor} reopened this`;
    case 'acknowledged':
      return `${actor} acknowledged this`;
    case 'assigned':
      return `${actor} queued this up`;
    case 'in_progress':
      return `${actor} started work on this`;
    case 'in_review':
      return `${actor} says this is fixed and needs checking`;
    case 'resolved':
      return `${actor} resolved this`;
    case 'wont_fix':
      return `${actor} marked this won't fix`;
  }
}

/** Colour role per status, so the widget, the dashboard and the project view all
 *  agree without each inventing its own palette. */
export function statusTone(status: Status): 'open' | 'progress' | 'review' | 'done' | 'dropped' {
  switch (status) {
    case 'open':
      return 'open';
    case 'acknowledged':
    case 'assigned':
    case 'in_progress':
      return 'progress';
    // Its own colour rather than another shade of "in flight". `in_review` is the
    // only state waiting on the person reading the screen, so it has to be
    // findable at a glance in a list of a hundred.
    case 'in_review':
      return 'review';
    case 'resolved':
      return 'done';
    case 'wont_fix':
      return 'dropped';
  }
}
